// @flow

import * as React from 'react';
import { get } from 'lodash';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { padding, shade } from 'polished';
import { useTheme } from 'emotion-theming';

import { hover } from 'app/utils/cssUtils';
import Responsive from 'app/components/responsive/Responsive';
import Clickable from 'app/components/commons/Clickable';
import { IN_PROGRESS, IN_REVIEW, COMPLETED } from '../../constants/challengeStatusTypes';
import { isInProgress, showCompleteChallengeStartTime } from '../../utils/challengeUtils';
import { rewritePath } from '../../utils/routeUtils';
import Icon from '../commons/Icon';
import RenderFirst from '../util/RenderFirst';
import ButtonWrapper from './ChallengeButtonWrapper';
import ChallengeEnterButton from './ChallengeEnterButton';
import ChallengeEntryState from './ChallengeEntryState';
import ChallengeIcons from './ChallengeIcons';
import ChallengeRowDetails from './ChallengeRowDetails';
import DateComponent from '../commons/DateComponent';
import Row from '../commons/Row';
import ScheduledTime from './ScheduledTime';
import StatusDot from '../commons/StatusDot';
import Counter from '../commons/Counter';
import Tooltip from '../commons/Tooltip';
import GoldTicketButton from './GoldTicketButton';

type Props = {
  challengeId: string,
  competitionIcon?: string,
  cost?: number,
  disableRowClick: boolean,
  discount?: number,
  entrantNames?: Array<string>,
  entryCap?: number,
  entryCount: number,
  expanded?: boolean,
  featured?: boolean,
  hasCaptain?: boolean,
  hasEntered?: boolean,
  historyPush: Function,
  isCasualContest?: boolean,
  isDeletingChallenges?: boolean,
  isDrop1?: boolean,
  isH2H?: boolean,
  isRapidfire?: boolean,
  isRestricted?: boolean,
  isPrivate?: boolean,
  isTargetChallenge?: boolean,
  isTicketChallenge?: boolean,
  isTrainingChallenge?: boolean, // eslint-disable-line
  isUnlimited: boolean,
  isVisible: boolean,
  link?: string,
  maxEntriesPerPerson?: number,
  name?: string,
  onCheckBoxClick?: (id: string) => void,
  onCounterClick?: (challengeId: string, direction: number) => void,
  onExpand?: string => void,
  onNameClick?: string => void,
  ownerName?: string,
  ownerType?: string,
  payout: string,
  payoutIcon?: string,
  payoutMessage?: string,
  payoutType?: string,
  pendingEntryAmount?: number,
  points?: number,
  position?: number,
  prizePool?: any,
  reducedCost?: number,
  rewriteRules: Object,
  selected?: boolean,
  showChallengeEntryState?: boolean,
  showCheckBox?: boolean,
  showCounter?: boolean,
  showDelete?: boolean,
  showEnter?: boolean,
  showExpand?: boolean,
  showPayout: boolean,
  showPlayerSelections?: boolean,
  showSportsIcon?: boolean,
  sport?: object,
  startTime: number,
  statusId: number,
  statusLabel: string,
  userEntryCount: number,
  userEntryIds?: Array<number>,
  winnings?: number,
  won?: boolean,
  rookieOnly?: boolean,
  isTwoThirdSize?: boolean
};

const Details = styled.div(({ theme }) => ({
  alignItems: 'stretch',
  display: 'flex',
  flexDirection: 'row',
  flexGrow: 1,
  justifyContent: 'space-between',
  paddingTop: theme.spacing.xsmall,
  paddingBottom: theme.spacing.xsmall
}));

const ChallengeNameButton = styled.button({
  padding: 0,
  textAlign: 'left',
  outline: 'none',
  ...hover({
    textDecoration: 'underline'
  })
});

const ButtonCell = styled.div(
  {
    height: '100%',
    alignItems: 'stretch',
    display: 'flex',
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 0,
    justifyContent: 'center'
  },
  ({ theme }) => ({
    paddingLeft: theme.spacing.small * 2
  })
);

const BaseButton = styled.div(
  ({ theme }) => ({
    display: 'flex',
    flexBasis: 'auto',
    borderRadius: '0.25em',
    ...padding('0.5em', '0.75em'),
    marginRight: '0.5em',
    textAlign: 'left',
    fontSize: '0.81rem',
    textTransform: 'uppercase',
    color: theme.color.white,
    minWidth: 80,
    backgroundColor: theme.color.basis2,
    ...hover({
      backgroundColor: theme.color.basis3
    }),
    [theme.mediaQuery.desktopAndUp]: {
      marginRight: 0,
      fontSize: '0.83rem',
      ...padding('0.25em', '0.5em')
    }
  }),
  ({ theme, live }) =>
    live
      ? {
          backgroundColor: theme.color.green,
          ...hover({
            backgroundColor: shade(0.8, theme.color.green)
          })
        }
      : {}
);

const ViewButton = styled(BaseButton)({
  '&::after': {
    content: '"❯"',
    marginLeft: 'auto'
  }
});

const ExpandButton = styled(BaseButton)(
  {
    '&::after': {
      content: '"❯"',
      marginLeft: 'auto',
      transition: 'all 0.3s ease-out'
    }
  },
  ({ expanded }) =>
    expanded
      ? {
          opacity: 0.7,
          '&::after': {
            transform: 'rotate(90deg) translate(0, 2px)'
          }
        }
      : {
          '&::after': {
            transform: 'rotate(0deg) translate(0, 0)'
          }
        }
);

const FlexTitle = styled.div(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  [theme.mediaQuery.mobileOnly]: {
    marginTop: -5
  }
}));

const RapidfireIcon = styled(Icon)(({ theme }) => ({
  color: theme.color.primary,
  fontSize: theme.fontSize.xlarge
}));

const SportsIconWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  ...padding(theme.spacing.small, 0, theme.spacing.small, theme.spacing.small),
  [theme.mediaQuery.mobileSmallOnly]: {
    ...padding(theme.spacing.tiny, 0, theme.spacing.tiny, theme.spacing.tiny)
  }
}));

const SportsIcon = styled(Icon)(({ theme }) => ({
  color: get(theme.color, 'sportsIcon', theme.color.primary),
  fontSize: theme.fontSize.xlarge
}));

const InvisibleIcon = styled(Icon)(({ theme }) => ({
  // color: theme.color.primary,
  fontSize: '2rem',
  paddingRight: theme.spacing.small,
  minWidth: '2rem'
}));

const EntrantNames = styled.span({
  fontSize: '0.9em',
  paddingLeft: '0.5em'
});

const RadioWrapper = styled(Clickable)({
  minWidth: 60,
  width: '100%',
  height: '100%'
});

const Radio = styled(({ className, selected }) => (
  <span
    className={`${className} ${
      selected ? 'ion-android-radio-button-on' : 'ion-android-radio-button-off'
    }`}
  />
))(({ theme }) => ({
  color: theme.color.primary,
  fontSize: '1.8rem'
}));

const MobileGoldTicketButton = styled(GoldTicketButton)(({ theme }) => ({
  display: 'flex',
  [theme.mediaQuery.mobileOnly]: {
    alignItems: 'flex-end'
  },
  padding: theme.spacing.small,
  paddingRight: 0
}));

const InnerRow = styled.div({
  display: 'flex',
  lineHeight: 1.4
});

const Info = styled.div(({ theme }) => ({
  display: 'flex',
  flexBasis: 'auto',
  flexGrow: 1,
  flexShrink: 1,
  paddingLeft: theme.spacing.small,
  [theme.mediaQuery.mobileSmallOnly]: {
    paddingLeft: theme.spacing.tiny
  }
}));

const InfoInner = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  flexShrink: 1,
  [theme.mediaQuery.desktopAndUp]: {
    alignItems: 'center',
    flexDirection: 'row'
  }
}));

const Status = styled.div(({ theme }) => ({
  paddingTop: theme.spacing.xsmall,
  paddingRight: theme.spacing.small,
  [theme.mediaQuery.desktopAndUp]: {
    alignSelf: 'flex-start'
  }
}));

const Summary = styled.div(({ theme, isTwoThirdSize }) => ({
  fontSize: '1.12em',
  maxWidth: 350, // hack to stop overlap with other cells
  paddingBottom: 3,
  textTransform: 'uppercase',
  [theme.mediaQuery.desktopAndUp]: {
    paddingBottom: 0,
    width: '50%'
  },
  [theme.mediaQuery.desktopLargeOnly]: {
    maxWidth: isTwoThirdSize ? 150 : 450
  },
  [theme.mediaQuery.desktopWide]: {
    maxWidth: isTwoThirdSize ? 250 : 450
  },
  [theme.mediaQuery.mobileOnly]: {
    display: 'flex',
    justifyContent: 'space-between',
    minWidth: '100%'
  }
}));

const TableCell = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 0,
  flexShrink: 0,
  flexBasis: 110,
  alignItems: 'flex-end',
  justifyContent: 'center',
  // paddingLeft: theme.spacing.small,
  paddingRight: theme.spacing.small,
  textTransform: 'uppercase',
  [theme.mediaQuery.desktopAndUp]: {
    alignItems: 'flex-start',
    flexBasis: 120
  }
}));

const ByLine = styled.div(({ theme }) => ({
  color: theme.color.basis4,
  fontWeight: theme.fontWeight.normal,
  fontSize: '0.8em',
  textTransform: 'none'
}));

const MobileTicket = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center'
});

const defaultProps = {
  competitionIcon: undefined,
  cost: undefined,
  discount: undefined,
  entrantNames: undefined,
  userEntryIds: undefined,
  entryCap: undefined,
  expanded: false,
  featured: false,
  hasCaptain: false,
  hasEntered: false,
  isCasualContest: false,
  isDeletingChallenges: false,
  isDrop1: false,
  isH2H: false,
  isRapidfire: false,
  isRestricted: false,
  isPrivate: false,
  isTargetChallenge: false,
  isTicketChallenge: false,
  isTrainingChallenge: false,
  link: undefined,
  maxEntriesPerPerson: undefined,
  name: '',
  onCheckBoxClick: undefined,
  onCounterClick: undefined,
  onExpand: undefined,
  onNameClick: undefined,
  ownerName: undefined,
  ownerType: undefined,
  payoutIcon: undefined,
  payoutMessage: undefined,
  payoutType: undefined,
  pendingEntryAmount: undefined,
  points: undefined,
  position: undefined,
  prizePool: undefined,
  reducedCost: undefined,
  selected: false,
  showChallengeEntryState: true,
  showCheckBox: false,
  showCounter: false,
  showDelete: false,
  showEnter: false,
  showExpand: false,
  showPlayerSelections: false,
  showSportsIcon: false,
  sport: undefined,
  winnings: undefined,
  won: false,
  isTwoThirdSize: false,
  rookieOnly: false
};

function ChallengeRow(props: Props) {
  const {
    challengeId,
    competitionIcon,
    cost,
    disableRowClick,
    discount,
    isDrop1,
    entrantNames,
    entryCap,
    entryCount,
    expanded,
    featured,
    hasCaptain,
    hasEntered = false,
    historyPush,
    isCasualContest,
    isDeletingChallenges,
    isH2H,
    isPrivate,
    isTargetChallenge,
    isTicketChallenge,
    isRapidfire,
    isRestricted,
    isUnlimited,
    isVisible,
    link,
    maxEntriesPerPerson,
    name,
    onCheckBoxClick,
    onCounterClick,
    onExpand,
    onNameClick,
    ownerName,
    ownerType,
    payout,
    payoutIcon,
    payoutMessage,
    payoutType,
    pendingEntryAmount,
    points,
    position,
    prizePool,
    reducedCost,
    rewriteRules,
    selected,
    showChallengeEntryState,
    showCheckBox,
    showCounter,
    showDelete,
    showEnter,
    showExpand,
    showPayout,
    showPlayerSelections,
    showSportsIcon,
    sport,
    startTime,
    statusId,
    statusLabel,
    userEntryCount,
    userEntryIds,
    winnings,
    won,
    isTwoThirdSize,
    rookieOnly
  } = props;
  const theme = useTheme();

  const handleExpand = event => {
    event.preventDefault();
    event.stopPropagation();

    onExpand(challengeId);
  };

  const openModal = event => {
    if (onNameClick) {
      event.preventDefault();
      event.stopPropagation();
      onNameClick(challengeId);
    }
  };

  const handleRowClick = e => {
    if (userEntryCount > 1) {
      handleExpand(e);
    } else {
      historyPush(`${rewritePath(rewriteRules)(link)}/${userEntryIds[0]}`);
    }
  };

  const radioEnabled =
    isH2H && entryCount < entryCap && !isDeletingChallenges && ownerType === 'users';
  const sportsIcon = get(sport, 'icon', 'all');

  const editableSingleEntry =
    maxEntriesPerPerson === 1 && userEntryIds.length === 1 ? `/${userEntryIds[0]}` : '';

  return (
    <Row
      style={{ backgroundColor: rookieOnly ? '#FFBDD3' : 'white' }}
      link={disableRowClick || onExpand ? undefined : link + editableSingleEntry}
      onTap={onExpand ? handleRowClick : undefined}
      active={won}
    >
      <InnerRow>
        {showChallengeEntryState && (
          <ChallengeEntryState
            entryCap={entryCap}
            entryCount={entryCount}
            isUnlimited={isUnlimited}
            maxEntriesPerPerson={maxEntriesPerPerson}
            statusId={statusId}
            userEntryCount={userEntryCount}
          />
        )}
        {isRapidfire && (
          <SportsIconWrapper>
            <RapidfireIcon type="icon_menu_rapidfire" />
          </SportsIconWrapper>
        )}
        {showSportsIcon && !isRapidfire && (
          <SportsIconWrapper>
            <SportsIcon
              color={theme.color.sportsIcon}
              image={competitionIcon}
              type={isTargetChallenge ? 'icon-target' : `icon-sport-${sportsIcon}`}
            />
          </SportsIconWrapper>
        )}
        <Details>
          <Info>
            {statusId >= IN_PROGRESS && (
              <Status>
                <StatusDot statusId={statusId} />
              </Status>
            )}
            <InfoInner>
              <Summary isTwoThirdSize={isTwoThirdSize}>
                <FlexTitle>
                  <ChallengeNameButton
                    type="button"
                    disabled={isRapidfire}
                    onClick={isRapidfire ? undefined : openModal}
                  >
                    {!isVisible && <InvisibleIcon type="icon-invisible" />}
                    {name}
                    <ChallengeIcons
                      rookieOnly={rookieOnly}
                      captain={hasCaptain}
                      drop1={isDrop1}
                      featured={featured}
                      isCasualContest={isCasualContest}
                      isRestricted={isRestricted}
                      isPrivate={isPrivate}
                      payoutMessage={payoutMessage}
                      payoutType={payoutType}
                      payoutIcon={payoutIcon}
                      showPlayerSelections={showPlayerSelections}
                    />
                  </ChallengeNameButton>
                </FlexTitle>
                {showCounter && (
                  <Responsive mobilePortrait>
                    <TableCell>
                      <Counter
                        value={pendingEntryAmount}
                        max={maxEntriesPerPerson - userEntryCount}
                        onPlus={() => {
                          onCounterClick(challengeId, 1);
                        }}
                        onMinus={() => {
                          onCounterClick(challengeId, -1);
                        }}
                      />
                    </TableCell>
                  </Responsive>
                )}
                {showCheckBox && (
                  <Responsive mobilePortrait>
                    <ButtonCell>
                      <RadioWrapper
                        onClick={() => {
                          onCheckBoxClick(challengeId);
                        }}
                        stopPropagation
                      >
                        <Radio selected={selected} />
                      </RadioWrapper>
                    </ButtonCell>
                  </Responsive>
                )}
                {showExpand && (
                  <Responsive mobilePortrait>
                    <ButtonWrapper>
                      <RenderFirst>
                        {showDelete && (
                          <RadioWrapper
                            disabled={!radioEnabled}
                            onClick={() => {
                              if (radioEnabled) onCheckBoxClick(challengeId);
                            }}
                            stopPropagation
                          >
                            <Radio selected={selected} />
                          </RadioWrapper>
                        )}
                        {userEntryCount > 1 && (
                          <ExpandButton
                            expanded={expanded}
                            live={isInProgress(statusId) || statusId === IN_REVIEW}
                          >
                            <RenderFirst>
                              {statusId < IN_PROGRESS && 'Edit'}
                              {statusId === COMPLETED && 'View'}
                              Watch
                            </RenderFirst>
                          </ExpandButton>
                        )}
                        {statusId < IN_PROGRESS && <ViewButton>Edit</ViewButton>}
                        <ViewButton live={isInProgress(statusId) || statusId === IN_REVIEW}>
                          <RenderFirst>
                            {statusId === COMPLETED && 'View'}
                            Watch
                          </RenderFirst>
                        </ViewButton>
                      </RenderFirst>
                    </ButtonWrapper>
                  </Responsive>
                )}
                {showEnter && !isTicketChallenge && (
                  <Responsive mobilePortrait>
                    <ChallengeEnterButton
                      entryCap={entryCap}
                      entryCount={entryCount}
                      isUnlimited={isUnlimited}
                      maxEntriesPerPerson={maxEntriesPerPerson}
                      statusId={statusId}
                      userEntryCount={userEntryCount}
                      hasBorder={false}
                    />
                  </Responsive>
                )}
                <Responsive desktopAndUp>
                  <ByLine>
                    {statusId < IN_PROGRESS && (
                      <ScheduledTime startTime={startTime} format="ddd h:mmA" />
                    )}
                    {(isInProgress(statusId) || statusId === IN_REVIEW) && (
                      <span>{statusLabel}</span>
                    )}
                    {showCompleteChallengeStartTime(statusId) && (
                      <DateComponent date={startTime} format="ddd DD MMM YYYY" />
                    )}
                    {entrantNames && <EntrantNames> ({entrantNames.join(', ')}) </EntrantNames>}
                    {ownerName && <EntrantNames> (created by {ownerName}) </EntrantNames>}
                  </ByLine>
                </Responsive>
              </Summary>
              <ChallengeRowDetails
                cost={cost}
                discount={discount}
                winnings={winnings}
                entryCap={entryCap}
                entryCount={isRapidfire ? undefined : entryCount}
                hasEntered={hasEntered}
                isLive={statusId >= IN_PROGRESS}
                isTicketChallenge={isTicketChallenge}
                isRapidfire={isRapidfire}
                isUnlimited={isUnlimited}
                maxEntriesPerPerson={maxEntriesPerPerson}
                payout={showPayout && !isRapidfire ? payout : undefined}
                payoutIcon={payoutIcon}
                payoutMessage={payoutMessage}
                points={points}
                position={isRapidfire ? undefined : position}
                prizePool={prizePool}
                reducedCost={reducedCost}
                scheduledTime={statusId < IN_PROGRESS ? startTime : undefined}
                statusLabel={
                  isInProgress(statusId) || statusId === IN_REVIEW ? statusLabel : undefined
                }
                startTime={showCompleteChallengeStartTime(statusId) ? startTime : undefined}
                showPrize={!hasEntered || statusId < IN_PROGRESS}
              />
            </InfoInner>
          </Info>
          {showCounter && (
            <Responsive mobileLandscape tabletLandscape tabletPortrait desktop desktopLarge>
              <TableCell>
                <Counter
                  value={pendingEntryAmount}
                  max={maxEntriesPerPerson - userEntryCount}
                  onPlus={() => {
                    onCounterClick(challengeId, 1);
                  }}
                  onMinus={() => {
                    onCounterClick(challengeId, -1);
                  }}
                />
              </TableCell>
            </Responsive>
          )}
          {showCheckBox && (
            <Responsive mobileLandscape tabletLandscape tabletPortrait desktop desktopLarge>
              <ButtonCell>
                <RadioWrapper
                  onClick={() => {
                    onCheckBoxClick(challengeId);
                  }}
                  stopPropagation
                >
                  <Radio selected={selected} />
                </RadioWrapper>
              </ButtonCell>
            </Responsive>
          )}
          {showExpand && (
            <Responsive mobileLandscape tabletLandscape tabletPortrait desktop desktopLarge>
              <ButtonWrapper>
                <RenderFirst>
                  {showDelete && (
                    <RadioWrapper
                      disabled={!radioEnabled}
                      onClick={() => {
                        if (radioEnabled) onCheckBoxClick(challengeId);
                      }}
                      stopPropagation
                    >
                      <Radio selected={selected} />
                    </RadioWrapper>
                  )}
                  {userEntryCount > 1 && (
                    <ExpandButton
                      expanded={expanded}
                      live={isInProgress(statusId) || statusId === IN_REVIEW}
                    >
                      <RenderFirst>
                        {statusId < IN_PROGRESS && 'Edit'}
                        {statusId === COMPLETED && 'View'}
                        Watch
                      </RenderFirst>
                    </ExpandButton>
                  )}
                  {statusId < IN_PROGRESS && <ViewButton>Edit</ViewButton>}
                  <ViewButton live={isInProgress(statusId) || statusId === IN_REVIEW}>
                    <RenderFirst>
                      {statusId === COMPLETED && 'View'}
                      Watch
                    </RenderFirst>
                  </ViewButton>
                </RenderFirst>
              </ButtonWrapper>
            </Responsive>
          )}
          {showEnter && (
            <Responsive mobileLandscape tabletLandscape tabletPortrait desktop desktopLarge>
              <ChallengeEnterButton
                entryCap={entryCap}
                entryCount={entryCount}
                isUnlimited={isUnlimited}
                maxEntriesPerPerson={maxEntriesPerPerson}
                statusId={statusId}
                userEntryCount={userEntryCount}
                hasBorder
              />
            </Responsive>
          )}
          {isTicketChallenge && !showExpand && !showCounter && (
            <Responsive mobilePortrait>
              <MobileTicket>
                <Tooltip title={payoutMessage} disabled={!payoutMessage}>
                  <MobileGoldTicketButton icon={payoutIcon} />
                </Tooltip>
              </MobileTicket>
            </Responsive>
          )}
        </Details>
      </InnerRow>
    </Row>
  );
}

ChallengeRow.defaultProps = defaultProps;

function mapStateToProps(state, { challengeId }: Props) {
  return {
    pendingEntryAmount: state.compatibleChallenges.pendingEntries[challengeId],
    rewriteRules: state.settings.rewriteRules
  };
}

export default connect(mapStateToProps)(ChallengeRow);
